/**
 * The filter field of a live dataset, for whichever source it reads: Langfuse's
 * filter box over observations, or qFlow's run filters. Shared by the create
 * dialog and the source's edit form.
 */

import { Field } from "@/components/ui/field";
import { useMatchCount } from "@/features/traces/hooks";
import { ObservationFilters } from "@/features/traces/observation-filters";
import { QflowFilters } from "@/features/traces/qflow-filters";
import { nodeRules } from "@/features/traces/qflow-query";
import { qflowWorkspace } from "@/features/traces/qflow-workspace";
import { useSourceKind } from "@/features/traces/sources";
import type { TraceQuery } from "@/features/traces/trace-query";
import { FilterHint } from "./filter-hint";

export function SourceFilters({
  sourceId,
  value,
  onChange,
  onBlocked,
}: {
  sourceId: string;
  value: TraceQuery;
  onChange: (next: TraceQuery) => void;
  onBlocked?: (why: string | null) => void;
}) {
  const { qflow } = useSourceKind(sourceId);
  // A qFlow count needs its workspace: none is asked for before one is picked.
  const count = useMatchCount(qflow && !qflowWorkspace(value) ? "" : sourceId, value);
  const hint = (
    <FilterHint
      matched={count.data?.matched}
      qflow={qflow}
      upperBound={qflow && nodeRules(value).length > 0}
    />
  );
  return (
    <Field label={qflow ? "Which runs" : "Which observations"} optional={!qflow} hint={hint}>
      {sourceId &&
        (qflow ? (
          // Keyed by source: another connection starts with no rules, as its query does.
          <QflowFilters
            key={sourceId}
            sourceId={sourceId}
            value={value}
            onChange={onChange}
            onBlocked={onBlocked}
          />
        ) : (
          <ObservationFilters sourceId={sourceId} value={value} onChange={onChange} nameButton={false} />
        ))}
    </Field>
  );
}
